import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import StatCard from '../../components/shared/StatCard';

const TIER_LABELS = ['', 'All Staff', 'Manager', 'Executive'];
const TIER_COLORS = ['', '#10b981', '#00bf63', '#f59e0b'];

const initialsFor = (name, surname) => {
  const parts = [name, surname].filter(Boolean);
  if (parts.length === 0) return '—';
  return parts.slice(0, 2).map(p => p[0]).join('').toUpperCase();
};

const TierBadge = ({ tier }) => {
  const t = Number(tier) || 1;
  return (
    <span className={`loysta-badge tier-${t}`}>
      <span className="dot" />
      T{t} · {TIER_LABELS[t]}
    </span>
  );
};

const CompanyDashboard = () => {
  const [companyData, setCompanyData] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    newThisMonth: 0,
    tierCounts: { 1: 0, 2: 0, 3: 0 },
  });
  const [recentEmployees, setRecentEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('companyData');
    if (!stored) {
      navigate('/company/login');
      return;
    }
    const company = JSON.parse(stored);
    setCompanyData(company);
    loadDashboardData(company.id);
  }, [navigate]);

  const loadDashboardData = async (companyId) => {
    try {
      const empSnap = await getDocs(query(collection(db, 'users'), where('companyId', '==', companyId)));
      const employees = empSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      const now = new Date();
      const month = now.getMonth();
      const year = now.getFullYear();

      let newThisMonth = 0;
      const tierCounts = { 1: 0, 2: 0, 3: 0 };
      employees.forEach((e) => {
        const t = Number(e.tier) || 1;
        if (tierCounts[t] !== undefined) tierCounts[t]++;
        if (e.createdAt?.toDate) {
          const d = e.createdAt.toDate();
          if (d.getMonth() === month && d.getFullYear() === year) newThisMonth++;
        }
      });

      const recent = employees
        .filter((e) => e.createdAt)
        .sort((a, b) => {
          const da = a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const dbb = b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return dbb - da;
        })
        .slice(0, 5);

      setStats({ total: employees.length, newThisMonth, tierCounts });
      setRecentEmployees(recent);
    } catch (err) {
      console.error('Error loading company dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatJoined = (timestamp) => {
    if (!timestamp) return '—';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const total = stats.total;
  const tierRows = [1, 2, 3].map((t) => {
    const n = stats.tierCounts[t] || 0;
    const pct = total === 0 ? 0 : Math.round((n / total) * 100);
    return { tier: t, n, pct };
  });

  if (loading) {
    return (
      <div style={{ padding: 40, color: '#64748b', fontSize: 14 }}>Loading dashboard…</div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Welcome */}
      <div>
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, marginBottom: 4 }}>
          Welcome back
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' }}>
          {companyData?.name || 'Your Company'}
        </div>
      </div>

      {/* Stat cards */}
      <div className="loysta-stat-grid">
        <StatCard
          icon="users"
          label="Total Employees"
          value={stats.total}
          sub={stats.newThisMonth > 0 ? `+${stats.newThisMonth} this month` : 'No new joiners this month'}
          variant="blue"
        />
        <StatCard
          icon="eye"
          label="Active This Month"
          value={stats.newThisMonth}
          sub={stats.total > 0 ? `${Math.round((stats.newThisMonth / stats.total) * 100)}% engagement` : '—'}
          variant="purple"
        />
        <StatCard
          icon="tag"
          label="Offers Redeemed"
          value="—"
          sub="Reporting coming soon"
          variant="green"
        />
        <StatCard
          icon="layers"
          label="Benefit Tiers"
          value="3"
          sub="All Staff · Manager · Executive"
          dark
        />
      </div>

      {/* Main grid */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        {/* Recent Employees */}
        <div className="loysta-card" style={{ flex: 1, minWidth: 0 }}>
          <div className="loysta-card-head">
            <span className="loysta-card-title">Recent Employees</span>
            <span
              className="loysta-card-link"
              onClick={() => navigate('/company/employees')}
              role="button"
            >View all</span>
          </div>
          {recentEmployees.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              No employees added yet.{' '}
              <span
                onClick={() => navigate('/company/add-employee')}
                style={{ color: '#00bf63', fontWeight: 600, cursor: 'pointer' }}
              >Add your first one</span>
            </div>
          ) : (
            <table className="loysta-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Tier</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {recentEmployees.map((e) => {
                  const t = Number(e.tier) || 1;
                  return (
                    <tr key={e.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/company/employees')}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 30, height: 30, borderRadius: '50%',
                            background: `linear-gradient(135deg, ${TIER_COLORS[t]}, ${t === 3 ? '#fbbf24' : t === 2 ? '#c1ff72' : '#6ee7b7'})`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, fontWeight: 700, color: '#0f172a', flexShrink: 0,
                          }}>{initialsFor(e.name, e.surname)}</div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                              {[e.name, e.surname].filter(Boolean).join(' ') || 'Unnamed'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ color: '#64748b', fontSize: 13 }}>{e.phone || '—'}</td>
                      <td><TierBadge tier={t} /></td>
                      <td style={{ color: '#94a3b8', fontSize: 12 }}>{formatJoined(e.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Right widgets */}
        <div style={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Tier Distribution */}
          <div className="loysta-card" style={{ padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>
              Tier Distribution
            </div>
            {tierRows.map(({ tier, n, pct }) => (
              <div key={tier} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 12 }}>
                  <span style={{ color: TIER_COLORS[tier], fontWeight: 600 }}>
                    Tier {tier} · {TIER_LABELS[tier]}
                  </span>
                  <span style={{ color: '#94a3b8' }}>{n} {n === 1 ? 'employee' : 'employees'}</span>
                </div>
                <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', background: TIER_COLORS[tier], borderRadius: 3,
                    width: `${pct}%`, transition: 'width 0.4s ease',
                  }} />
                </div>
              </div>
            ))}
          </div>

          {/* Quick action */}
          <div style={{
            background: '#0f172a',
            borderRadius: 12,
            padding: 18,
            position: 'relative',
            overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', top: -20, right: -20,
              width: 100, height: 100, borderRadius: '50%',
              background: 'rgba(193,255,114,0.07)', pointerEvents: 'none',
            }} />
            <div style={{ fontSize: 13, fontWeight: 700, color: 'white', marginBottom: 4 }}>
              Need to onboard someone?
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 14, lineHeight: 1.5 }}>
              Add a new employee in seconds — they'll get their QR code instantly.
            </div>
            <button
              type="button"
              onClick={() => navigate('/company/add-employee')}
              style={{
                width: '100%',
                padding: '9px',
                background: 'rgba(193,255,114,0.12)',
                border: '1px solid rgba(193,255,114,0.25)',
                borderRadius: 7,
                color: '#c1ff72',
                fontSize: 12,
                fontWeight: 700,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(193,255,114,0.20)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(193,255,114,0.12)'; }}
            >Add Employee</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDashboard;
